import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ALL_STORIES } from '@/mocks/stories';

export default function StoryPage() {
  const { id } = useParams<{ id: string }>();
  const story = ALL_STORIES.find(s => s.id === id) || ALL_STORIES[0];
  const [activeEp, setActiveEp] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [payMethod, setPayMethod] = useState('alipay');
  const [paySubmitted, setPaySubmitted] = useState(false);
  const [payForm, setPayForm] = useState({ email: '' });
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); setActiveEp(1); setIsPlaying(false); }, [id]);

  useEffect(() => {
    if (isPlaying) {
      progressRef.current = setInterval(() => setProgress(p => Math.min(p + 0.5, 100)), 200);
    } else {
      if (progressRef.current) clearInterval(progressRef.current);
    }
    return () => { if (progressRef.current) clearInterval(progressRef.current); };
  }, [isPlaying]);

  const currentEp = story.episodes.find(e => e.ep === activeEp) || story.episodes[0];
  const isFree = currentEp.free;
  const previewContent = !isFree
    ? currentEp.content.slice(0, Math.max(50, Math.floor(currentEp.content.length * 0.1))) + '……'
    : currentEp.content;

  const handlePaySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPaySubmitted(true);
  };

  const handleEpClick = (ep: number, free: boolean) => {
    setActiveEp(ep);
    setIsPlaying(false);
    setProgress(0);
    if (!free) setShowPaywall(true);
  };

  return (
    <div className="min-h-screen" style={{ background: '#060A14', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: 'rgba(6,10,20,0.95)', borderColor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-white/50 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line" />返回首页
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${story.color}20`, border: `1px solid ${story.color}40`, color: story.color }}>{story.ep}</span>
            <span className="text-white/60 text-sm font-medium hidden sm:block">{story.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setIsPlaying(!isPlaying); if (!isFree && !isPlaying) setShowPaywall(true); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap"
              style={{ background: `${story.color}15`, border: `1px solid ${story.color}40`, color: story.color }}
            >
              <i className={isPlaying ? 'ri-pause-line' : 'ri-play-line'} />
              {isPlaying ? '暂停收听' : '在线收听'}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative" style={{ height: '300px' }}>
        <img src={story.img} alt={story.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060A14] via-[#060A14]/60 to-transparent" />
        <div className="absolute bottom-8 left-0 right-0 max-w-[1200px] mx-auto px-6">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${story.color}25`, border: `1px solid ${story.color}50`, color: story.color }}>{story.ep} · {story.tech}</span>
            <span className="text-white/40 text-xs">{story.year} · 共{story.totalEpisodes}集</span>
            <span className="text-white/40 text-xs">作者：{story.author}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>{story.title}</h1>
          <p className="text-white/60 text-sm">{story.teaser}</p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Episode List */}
          <div className="lg:col-span-1">
            <h3 className="text-white/60 text-xs font-bold tracking-widest uppercase mb-4">集数列表</h3>
            <div className="flex flex-col gap-2">
              {story.episodes.map(ep => (
                <button
                  key={ep.ep}
                  onClick={() => handleEpClick(ep.ep, ep.free)}
                  className="flex items-center gap-3 p-3 rounded-xl text-left cursor-pointer transition-all"
                  style={{ background: activeEp === ep.ep ? `${story.color}15` : 'rgba(255,255,255,0.03)', border: `1px solid ${activeEp === ep.ep ? story.color + '40' : 'rgba(255,255,255,0.08)'}` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold" style={{ background: ep.free ? `${story.color}20` : 'rgba(255,215,0,0.15)', color: ep.free ? story.color : '#FFD700' }}>
                    {ep.free ? ep.ep : <i className="ri-lock-line text-xs" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-xs font-semibold truncate">{ep.title}</p>
                    {!ep.free && <p className="text-[#FFD700] text-xs">¥8.88 解锁</p>}
                  </div>
                </button>
              ))}
            </div>

            {/* Unlock card */}
            <div className="mt-4 p-4 rounded-2xl" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.25)' }}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-vip-crown-line text-[#FFD700]" />
                <span className="text-[#FFD700] font-bold text-sm">全集解锁</span>
              </div>
              <p className="text-white/50 text-xs mb-3">一次性解锁本故事全部{story.totalEpisodes}集</p>
              <p className="text-[#FFD700] font-black text-xl mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>¥8.88</p>
              <button onClick={() => setShowPaywall(true)} className="w-full py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B35)', color: '#000' }}>
                立即解锁全集
              </button>
            </div>

            {/* Upload link */}
            <Link to="/story-upload" className="mt-3 flex items-center gap-2 p-3 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ background: 'rgba(0,255,204,0.08)', border: '1px solid rgba(0,255,204,0.2)', color: '#00FFCC' }}>
              <i className="ri-upload-cloud-line" />上传我的故事 · 赚60%收益
            </Link>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${story.color}20`, border: `1px solid ${story.color}40`, color: story.color }}>第{activeEp}集</span>
                <h2 className="text-white font-bold text-xl">{currentEp.title}</h2>
                {!isFree && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>
                    <i className="ri-lock-line mr-1" />付费内容
                  </span>
                )}
              </div>

              {/* Audio player */}
              {isPlaying && (
                <div className="mb-4 p-4 rounded-2xl flex items-center gap-4" style={{ background: `${story.color}10`, border: `1px solid ${story.color}30` }}>
                  <button onClick={() => setIsPlaying(false)} className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0" style={{ background: story.color, color: '#000' }}>
                    <i className="ri-pause-line" />
                  </button>
                  <div className="flex-1">
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                      <div className="h-full rounded-full transition-all duration-200" style={{ width: `${progress}%`, background: story.color }} />
                    </div>
                    <p className="text-white/40 text-xs mt-1">正在收听 · {currentEp.title}</p>
                  </div>
                  {!isFree && <span className="text-[#FFD700] text-xs font-bold whitespace-nowrap">仅前10%免费</span>}
                </div>
              )}

              {/* Story content */}
              <div className="text-white/75 text-sm leading-8 whitespace-pre-line" style={{ fontFamily: 'Georgia, serif' }}>
                {previewContent}
              </div>

              {/* Paywall overlay */}
              {!isFree && (
                <div className="mt-6 p-8 rounded-2xl text-center" style={{ background: 'linear-gradient(to bottom, rgba(6,10,20,0.5), rgba(6,10,20,0.98))', border: '1px solid rgba(255,215,0,0.25)' }}>
                  <i className="ri-lock-line text-3xl text-[#FFD700] block mb-3" />
                  <h3 className="text-white font-bold text-lg mb-2">解锁完整内容</h3>
                  <p className="text-white/50 text-sm mb-4">仅需 <span className="text-[#FFD700] font-black">¥8.88</span> 即可解锁本故事全部{story.totalEpisodes}集</p>
                  <button onClick={() => setShowPaywall(true)} className="px-8 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B35)', color: '#000' }}>
                    <i className="ri-vip-crown-line mr-2" />¥8.88 解锁全集
                  </button>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <button
                onClick={() => handleEpClick(Math.max(1, activeEp - 1), story.episodes.find(e => e.ep === Math.max(1, activeEp - 1))?.free ?? true)}
                disabled={activeEp === 1}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm cursor-pointer disabled:opacity-30 whitespace-nowrap"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
              >
                <i className="ri-arrow-left-line" />上一集
              </button>
              <span className="text-white/40 text-xs">{activeEp} / {story.episodes.length}</span>
              <button
                onClick={() => { const next = activeEp + 1; if (next <= story.episodes.length) { const nextEp = story.episodes.find(e => e.ep === next); handleEpClick(next, nextEp?.free ?? true); } }}
                disabled={activeEp === story.episodes.length}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm cursor-pointer disabled:opacity-30 whitespace-nowrap"
                style={{ background: `${story.color}15`, border: `1px solid ${story.color}40`, color: story.color }}
              >
                下一集<i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }} onClick={() => { setShowPaywall(false); setPaySubmitted(false); }}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: '#0a0e1a', border: '1px solid rgba(255,215,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(255,107,53,0.05))' }}>
              <i className="ri-vip-crown-line text-4xl text-[#FFD700] block mb-3" />
              <h3 className="text-white font-black text-xl mb-2">解锁完整故事</h3>
              <p className="text-white/60 text-sm">{story.title} · 全{story.totalEpisodes}集完整内容</p>
              <p className="text-[#FFD700] font-black text-4xl mt-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>¥8.88</p>
              <p className="text-white/40 text-xs mt-1">一次付费，永久解锁</p>
            </div>
            {paySubmitted ? (
              <div className="p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: 'rgba(0,255,136,0.15)' }}>
                  <i className="ri-check-line text-2xl" style={{ color: '#00FF88' }} />
                </div>
                <h4 className="text-white font-bold text-lg mb-2">支付成功！</h4>
                <p className="text-white/50 text-sm">已解锁{story.title}全部内容，请尽情阅读！</p>
                <button onClick={() => { setShowPaywall(false); setPaySubmitted(false); }} className="mt-4 px-6 py-2.5 rounded-xl text-sm font-bold cursor-pointer" style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00FF88' }}>开始阅读</button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handlePaySubmit} className="p-6 space-y-4">
                <div>
                  <label className="text-white/60 text-xs font-semibold block mb-2">邮箱（用于发送解锁凭证）</label>
                  <input name="email" type="email" value={payForm.email} onChange={e => setPayForm(f => ({ ...f, email: e.target.value }))} required placeholder="请输入邮箱" className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold block mb-2">支付方式</label>
                  <div className="flex gap-3">
                    {[{ id: 'alipay', label: '支付宝国际', icon: 'ri-alipay-line' }, { id: 'card', label: '信用卡', icon: 'ri-bank-card-line' }].map(pm => (
                      <button key={pm.id} type="button" onClick={() => setPayMethod(pm.id)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer"
                        style={{ background: payMethod === pm.id ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)', border: `1.5px solid ${payMethod === pm.id ? 'rgba(255,215,0,0.5)' : 'rgba(255,255,255,0.1)'}`, color: payMethod === pm.id ? '#FFD700' : '#fff' }}>
                        <i className={pm.icon} />{pm.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" className="w-full py-4 rounded-2xl font-black text-base cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B35)', color: '#000', fontFamily: 'Orbitron, sans-serif', fontSize: '0.85rem' }}>
                  立即支付 ¥8.88 →
                </button>
                <button type="button" onClick={() => setShowPaywall(false)} className="w-full py-2 text-white/30 text-sm cursor-pointer">取消</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
